import React, { useState, useEffect } from 'react';
import DocumentReviewModal from './DocumentReviewModal';
import ShareRequestModal from './ShareRequestModal';
import RequestDocumentsModal from './RequestDocumentsModal';
import ManualDocumentUploadModal from './ManualDocumentUploadModal';
import DirectDocumentUploadModal from './DirectDocumentUploadModal';
import { 
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  PencilIcon,
  ShareIcon,
  XMarkIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';

interface DocumentSubmission {
  id: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  gcsFilePath: string;
  gcsBucket: string;
  uploadedAt: string;
  note?: string;
}

interface DocumentRequestItem {
  id: number;
  requestId: number;
  documentTypeId: number;
  status: 'pending' | 'submitted' | 'accepted' | 'rejected';
  submittedAt?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  documentType: {
    name: string;
    description?: string;
    isRequired: boolean;
    maxFileSizeMb: number;
    allowedFileTypes: string[];
  };
  submissions?: DocumentSubmission[];
}

interface DocumentRequest {
  id: number;
  organizationId: number;
  leadId: number;
  requestToken: string;
  status: 'requested' | 'submitted' | 'reviewed' | 'rejected';
  requestedBy: number;
  requestedAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: number;
  reviewNotes?: string;
  isReRequest?: boolean;
  createdAt: string;
  updatedAt: string;
  items: DocumentRequestItem[];
  lead: {
    firstName: string;
    lastName: string;
    email: string;
  };
  requestedByUser: {
    firstName: string;
    lastName: string;
  };
  reviewedByUser?: {
    firstName: string;
    lastName: string;
  };
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  isRequired: boolean;
  maxFileSizeMb: number;
  allowedFileTypes: string[];
}

interface LeadDocumentsTabProps {
  leadId: string;
  leadName: string;
  leadEmail: string;
}

const LeadDocumentsTab: React.FC<LeadDocumentsTabProps> = ({ leadId, leadName, leadEmail }) => {
  const [documentRequests, setDocumentRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{ url: string; fileName: string; fileType: string } | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DocumentRequest | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'accepted' | 'requested'>('accepted');
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedRequestForShare, setSelectedRequestForShare] = useState<DocumentRequest | null>(null);
  const [showManualUploadModal, setShowManualUploadModal] = useState(false);
  const [selectedRequestForUpload, setSelectedRequestForUpload] = useState<DocumentRequest | null>(null);
  const [showDirectUploadModal, setShowDirectUploadModal] = useState(false);

  useEffect(() => {
    fetchDocumentRequests();
  }, [leadId]);

  const fetchDocumentRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await import('../services/api').then(module => 
        module.default.get(`/documents/requests?leadId=${leadId}&limit=100`)
      );

      if (response.data.success) {
        setDocumentRequests(response.data.data);
      } else {
        setError('Failed to fetch document requests');
      }
    } catch (error: any) {
      console.error('Error fetching document requests:', error);
      setError(error.response?.data?.message || 'Error fetching document requests');
    } finally {
      setLoading(false);
    }
  };


  const handleReviewRequest = (request: DocumentRequest) => {
    setSelectedRequest(request);
    setShowReviewModal(true);
  };

  const handleShareRequest = (request: DocumentRequest) => {
    setSelectedRequestForShare(request);
    setShowShareModal(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '') {
      return 'Invalid Date';
    }
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'rejected':
        return <XCircleIcon className="w-4 h-4 text-red-600" />;
      case 'submitted':
        return <ClockIcon className="w-4 h-4 text-blue-600" />;
      case 'pending':
        return <ExclamationTriangleIcon className="w-4 h-4 text-gray-400" />;
      default:
        return <ExclamationTriangleIcon className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePreviewFile = async (submission: DocumentSubmission) => {
    try {
      // Get signed URL for the file
      const response = await import('../services/api').then(module => 
        module.default.get(`/documents/files/${submission.fileName}/download`)
      );

      if (response.data.success) {
        setPreviewFile({
          url: response.data.data.signedUrl,
          fileName: submission.fileName,
          fileType: submission.fileType
        });
      }
    } catch (error) {
      console.error('Error getting file preview:', error);
      setError('Error loading file preview');
    }
  };

  const handleDownloadFile = async (submission: DocumentSubmission) => {
    try {
      // Get signed URL for the file
      const response = await import('../services/api').then(module => 
        module.default.get(`/documents/files/${submission.fileName}/download`)
      );

      if (response.data.success) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = response.data.data.signedUrl;
        link.download = submission.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Error downloading file');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Documents</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDocumentRequests}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Flatten all document items from all requests (including those without submissions)
  const allDocumentItems = documentRequests.flatMap(request => {
    // Add null check for items
    if (!request.items || !Array.isArray(request.items)) {
      return [];
    }
    
    return request.items.map(item => ({
      id: item.id,
      documentType: item.documentType,
      status: item.status,
      requestId: request.id,
      requestStatus: request.status,
      submittedAt: item.submittedAt,
      reviewedAt: item.reviewedAt,
      rejectionReason: item.rejectionReason,
      createdAt: item.createdAt,
      requestCreatedAt: request.requestedAt,
      submissions: item.submissions || []
    }));
  });

  // Filter accepted documents for the Accepted tab
  const acceptedDocuments = allDocumentItems.filter(item => item.status === 'accepted');

  // Filter document requests that need review for the Requested tab
  const pendingRequests = documentRequests.filter(request => 
    request.status === 'requested' || 
    request.status === 'submitted' || 
    (request.items && request.items.some(item => 
      item.status === 'pending' || 
      item.status === 'submitted' || 
      item.status === 'rejected'
    ))
  );

  // Accepted Documents View Component
  const AcceptedDocumentsView = ({ acceptedDocuments, onDownloadFile, onPreviewFile }: {
    acceptedDocuments: any[];
    onDownloadFile: (submission: any) => void;
    onPreviewFile: (submission: any) => void;
  }) => {
    if (acceptedDocuments.length === 0) {
      return (
        <div className="text-center py-8">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Accepted Documents</h3>
          <p className="text-gray-600">No documents have been accepted yet.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {acceptedDocuments.map((item) => (
          <div key={`${item.requestId}-${item.id}`} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="w-8 h-8 text-green-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-sm font-medium text-gray-900">
                      {item.documentType.name}
                    </h4>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      accepted
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    {item.submissions.length > 0 && (
                      <>
                        <span>{formatFileSize(item.submissions[0].fileSize)}</span>
                        <span>•</span>
                        <span>{formatDate(item.submissions[0].uploadedAt)}</span>
                      </>
                    )}
                  </div>
                  {item.submissions.length > 0 && item.submissions[0].note && (
                    <p className="text-sm text-gray-600 mt-1 italic">"{item.submissions[0].note}"</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {item.submissions.length > 0 && (
                  <>
                    {(item.submissions[0].fileType.startsWith('image/') || item.submissions[0].fileType === 'application/pdf') && (
                      <button
                        onClick={() => onPreviewFile(item.submissions[0])}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        Preview
                      </button>
                    )}
                    <button
                      onClick={() => onDownloadFile(item.submissions[0])}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                      Download
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleManualUpload = (request: DocumentRequest) => {
    setSelectedRequestForUpload(request);
    setShowManualUploadModal(true);
  };

  // Requested Documents View Component (similar to main Documents page)
  const RequestedDocumentsView = ({ pendingRequests, onReviewRequest, onShareRequest, onManualUpload }: {
    pendingRequests: DocumentRequest[];
    onReviewRequest: (request: DocumentRequest) => void;
    onShareRequest: (request: DocumentRequest) => void;
    onManualUpload: (request: DocumentRequest) => void;
  }) => {
    if (pendingRequests.length === 0) {
      return (
        <div className="text-center py-8">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Requests</h3>
          <p className="text-gray-600">No document requests are waiting for review.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {pendingRequests.map((request) => {
          // Calculate progress for each request
          const totalItems = request.items?.length || 0;
          const submittedItems = request.items?.filter(item => item.status === 'submitted').length || 0;
          const progressPercentage = totalItems > 0 ? (submittedItems / totalItems) * 100 : 0;
          const isPartialSubmission = submittedItems > 0 && submittedItems < totalItems;
          
          return (
          <div key={request.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="text-lg font-medium text-gray-900">
                    Request {request.id}
                  </h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    request.status === 'submitted' ? 'bg-yellow-100 text-yellow-800' : 
                    request.status === 'requested' ? 'bg-blue-100 text-blue-800' : 
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {request.status}
                  </span>
                  {request.isReRequest && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      Re-Request
                    </span>
                  )}
                  {isPartialSubmission && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      Partial
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  Requested on {formatDate(request.requestedAt)}
                </p>
                
                {/* Progress Bar */}
                {totalItems > 0 && request.status !== 'reviewed' && request.status !== 'rejected' && (
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 max-w-md">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            progressPercentage === 100 
                              ? 'bg-gradient-to-r from-green-500 to-green-600' 
                              : 'bg-gradient-to-r from-blue-500 to-blue-600'
                          }`}
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                      {submittedItems}/{totalItems} docs
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => onShareRequest(request)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ShareIcon className="w-4 h-4 mr-1" />
                  Share
                </button>
                {(request.status === 'requested' || request.status === 'submitted') && (
                  <button
                    onClick={() => onManualUpload(request)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <CloudArrowUpIcon className="w-4 h-4 mr-1" />
                    Upload
                  </button>
                )}
                {(request.status === 'submitted' || (request.items && request.items.some(item => item.status === 'submitted'))) && (
                  <button
                    onClick={() => onReviewRequest(request)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Review
                  </button>
                )}
              </div>
            </div>
            
            {/* Document Items */}
            <div className="space-y-3">
              {request.items?.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center space-x-3">
                    <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.documentType.name}</p>
                      <p className="text-xs text-gray-500">
                        Status: <span className={`font-medium ${getStatusColor(item.status)}`}>{item.status}</span>
                      </p>
                    </div>
                  </div>
                  {item.submissions && item.submissions.length > 0 && item.submissions[0] && (
                    <div className="text-xs text-gray-500">
                      {formatFileSize(item.submissions[0].fileSize)} • {formatDate(item.submissions[0].uploadedAt)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          );
        })}
      </div>
    );
  };

  if (allDocumentItems.length === 0) {
    return (
      <>
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Found</h3>
          <p className="text-gray-600 mb-4">This lead hasn't uploaded any documents yet.</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowDirectUploadModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-2" />
              Quick Upload
            </button>
            <button
              onClick={() => setShowRequestModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Request Documents
            </button>
          </div>
        </div>

        {/* Request Documents Modal */}
        {showRequestModal && (
          <RequestDocumentsModal
            onClose={() => setShowRequestModal(false)}
            onRequestCreated={async () => {
              await fetchDocumentRequests();
              setShowRequestModal(false);
            }}
            preSelectedLeadId={leadId}
            lockLeadSelection={true}
          />
        )}

        {/* Direct Document Upload Modal */}
        <DirectDocumentUploadModal
          isOpen={showDirectUploadModal}
          onClose={() => setShowDirectUploadModal(false)}
          onSuccess={async () => {
            await fetchDocumentRequests();
          }}
          preSelectedLeadId={parseInt(leadId)}
          preSelectedLeadName={leadName}
          lockLeadSelection={true}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Documents List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <DocumentTextIcon className="w-5 h-5 mr-2" />
            Documents
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDirectUploadModal(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-2" />
              Quick Upload
            </button>
            <button
              onClick={() => setShowRequestModal(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Request Documents
            </button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveSubTab('accepted')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSubTab === 'accepted'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Accepted ({acceptedDocuments.length})
            </button>
            <button
              onClick={() => setActiveSubTab('requested')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSubTab === 'requested'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Requested ({pendingRequests.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeSubTab === 'accepted' ? (
            <AcceptedDocumentsView 
              acceptedDocuments={acceptedDocuments}
              onDownloadFile={handleDownloadFile}
              onPreviewFile={handlePreviewFile}
            />
          ) : (
            <RequestedDocumentsView 
              pendingRequests={pendingRequests}
              onReviewRequest={handleReviewRequest}
              onShareRequest={handleShareRequest}
              onManualUpload={handleManualUpload}
            />
          )}
        </div>
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {previewFile.fileName}
              </h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {previewFile.fileType.startsWith('image/') ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.fileName}
                  className="max-w-full max-h-[70vh] mx-auto"
                />
              ) : previewFile.fileType === 'application/pdf' ? (
                <iframe
                  src={previewFile.url}
                  className="w-full h-[70vh] border-0"
                  title={previewFile.fileName}
                />
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Preview not available for this file type</p>
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = previewFile.url;
                      link.download = previewFile.fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Request Documents Modal */}
      {showRequestModal && (
        <RequestDocumentsModal
          onClose={() => setShowRequestModal(false)}
          onRequestCreated={async () => {
            await fetchDocumentRequests();
            setShowRequestModal(false);
          }}
          preSelectedLeadId={leadId}
          lockLeadSelection={true}
        />
      )}

      {/* Review Modal */}
      {showReviewModal && selectedRequest && (
        <DocumentReviewModal
          request={selectedRequest}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedRequest(null);
          }}
          onRequestUpdated={async () => {
            // Refresh the document requests after review is updated
            await fetchDocumentRequests();
          }}
        />
      )}

      {/* Share Request Modal */}
      {showShareModal && selectedRequestForShare && (
        <ShareRequestModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedRequestForShare(null);
          }}
          requestToken={selectedRequestForShare.requestToken}
          leadName={`${(selectedRequestForShare as any).lead?.firstName || ''} ${(selectedRequestForShare as any).lead?.lastName || ''}`}
          leadEmail={(selectedRequestForShare as any).lead?.email || ''}
          documentTypes={selectedRequestForShare.items?.map(item => item.documentType.name) || []}
        />
      )}

      {/* Manual Upload Modal */}
      {showManualUploadModal && selectedRequestForUpload && (
        <ManualDocumentUploadModal
          request={selectedRequestForUpload}
          onClose={() => {
            setShowManualUploadModal(false);
            setSelectedRequestForUpload(null);
          }}
          onUploadSuccess={async () => {
            await fetchDocumentRequests();
          }}
        />
      )}

      {/* Direct Document Upload Modal */}
      <DirectDocumentUploadModal
        isOpen={showDirectUploadModal}
        onClose={() => setShowDirectUploadModal(false)}
        onSuccess={async () => {
          await fetchDocumentRequests();
        }}
        preSelectedLeadId={parseInt(leadId)}
        preSelectedLeadName={leadName}
        lockLeadSelection={true}
      />
    </div>
  );
};

export default LeadDocumentsTab;
